import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Attribute, IconData } from "lib/api";

import ConfirmationDialog from "components/common/ConfirmationDialog";
import SelectLocationDialog, {
  LocationFormData,
} from "components/common/SelectLocationDialog";

import {
  Query,
  QueryGroup,
  QueryRule,
  QueryBuilderContext,
  Action,
  QueryNode,
  QueryBuilderLabels,
  QueryGroupCombinator,
  AskAction,
  QueryGroupOperator,
  SelectLocation,
} from "./types";

import Context from "./Context";

import RuleGroup from "./Rule/RuleGroup";

const emptyRule = (): QueryRule => ({
  id: Math.random(),
  operator: undefined,
});

const emptyGroup = (createEmptyRule = false): QueryGroup => ({
  id: Math.random(),
  combinator: "and",
  rules: createEmptyRule ? [emptyRule()] : [],
});

const findNodeById = (id: number, node: QueryNode): QueryNode | null => {
  if (node.id === id) {
    return node;
  }
  if ("rules" in node) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < node.rules.length; ++i) {
      const rule = node.rules[i];

      const found = findNodeById(id, rule);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

const findParentById = (
  id: number,
  node: QueryNode,
  parent?: QueryGroup
): QueryGroup | null => {
  if (node.id === id) {
    return parent ?? null;
  }

  if ("rules" in node) {
    const group = node as QueryGroup;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < group.rules.length; ++i) {
      const rule = group.rules[i];

      const found = findParentById(id, rule, group);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

function reducer(state: Query, action: Action) {
  const query = { ...state };

  switch (action.type) {
    case "reset-query": {
      return emptyGroup();
    }
    case "reset-group": {
      const group = findNodeById(action.id, query);
      if (group && "rules" in group) {
        group.rules = [emptyRule()];

        return query;
      }
      break;
    }
    case "add-rule": {
      const group = findNodeById(action.id, query);
      if (group && "rules" in group) {
        const rule: QueryRule = emptyRule();

        if (group.combinator === "or") {
          const simblingRule = group.rules.find(
            (r) => "attribute" in r && r.attribute != null
          ) as QueryRule | undefined;
          rule.attribute = simblingRule?.attribute;
        }

        group.rules.push(rule);

        return query;
      }
      break;
    }
    case "add-group": {
      const group = findNodeById(action.id, query);
      if (group && "rules" in group) {
        group.rules.push(emptyGroup(true));

        return query;
      }
      break;
    }
    case "remove-node": {
      const parent = findParentById(action.id, query);
      if (parent) {
        parent.rules = parent.rules.filter((rule) => rule.id !== action.id);

        return query;
      }

      // reset query if deleting top node
      return emptyGroup();
    }
    case "set-combinator": {
      const node = findNodeById(action.id, query);
      if (node && "combinator" in node) {
        if (node.combinator !== action.combinator) {
          node.combinator = action.combinator;

          node.rules = node.rules.map((r) => {
            const subRule = { ...r };
            if ("operator" in subRule) {
              delete subRule.operator;
              delete subRule.value;
            }

            return subRule;
          });

          return query;
        }
      }
      break;
    }
    case "set-attribute": {
      const node = findNodeById(action.id, query) as QueryRule;
      if (node && node.attribute !== action.attribute) {
        node.attribute = action.attribute ?? undefined;

        // we keep the operator and value unless the operator is explicitly null (or set)
        if (action.operator !== undefined) {
          node.operator = action.operator ?? undefined;
          node.value = action.value ?? undefined;
        }

        const parent = findParentById(action.id, query);
        if (
          parent != null &&
          parent.combinator === "or" &&
          parent.rules.length >= 1
        ) {
          parent.rules = parent.rules.map((rule) => {
            if ("attribute" in rule) {
              return {
                id: rule.id,
                attribute: node.attribute,
                operator: node.operator != null ? rule.operator : undefined,
                value: node.value != null ? rule.value : undefined,
              };
            }

            return rule;
          });
        }

        return query;
      }
      break;
    }
    case "set-operator": {
      const node = findNodeById(action.id, query);
      if (node && "attribute" in node) {
        if (node.operator !== action.operator) {
          node.operator = action.operator as string;
          if (action.value !== undefined) {
            node.value = action.value ?? undefined;
          }

          return query;
        }
      }
      break;
    }
    case "set-value": {
      const node = findNodeById(action.id, query);
      if (node && "operator" in node) {
        node.value = action.value ?? undefined;

        return query;
      }
      break;
    }

    default:
  }

  // just return existing state if nothing changed
  return state;
}

function clearNodeIds(original: QueryNode): QueryNode {
  const rule = { ...original };

  delete rule.id;

  if ("rules" in rule) {
    rule.rules = rule.rules.map((r) => clearNodeIds(r));
  }

  return rule;
}

export interface QueryBuilderProps {
  attributes: Record<string, Attribute>;
  query?: Query;
  onChange?: (value: Query) => void;
  operators?: Record<string, QueryGroupOperator[]>;
  combinators?: QueryGroupCombinator[];
  maxDepth?: number;
  labels?: QueryBuilderLabels;
  marker?: IconData;
}

const QueryBuilder = ({
  attributes,
  query,
  onChange,
  operators,
  combinators,
  maxDepth = 1,
  labels,
  marker,
}: QueryBuilderProps): JSX.Element => {
  const [state, dispatchAction] = useReducer(reducer, query, (initialState) => {
    return initialState || emptyGroup();
  });

  const [pendingAction, askAction] = useState<AskAction>();
  const [locationDialog, selectLocation] = useState<SelectLocation>();

  const currentAttributes = useRef<Record<string, Attribute>>();
  useEffect(() => {
    if (currentAttributes.current == null) {
      // first run, nothing to do
      currentAttributes.current = attributes;
    } else if (currentAttributes.current !== attributes) {
      // attributes changed, the existing query is almost certain invalid, so reset it
      currentAttributes.current = attributes;
      dispatchAction({ type: "reset-query" });
    }
  }, [attributes]);

  const parentOrDefaultContext = React.useContext(Context);

  const originalState = query === state;
  const context = useMemo(
    (): QueryBuilderContext => ({
      dispatchAction,
      askAction,
      selectLocation,
      attributes,
      operators: operators ?? parentOrDefaultContext.operators,
      combinators: combinators ?? parentOrDefaultContext.combinators,
      maxDepth: maxDepth ?? parentOrDefaultContext.maxDepth,
      labels: labels ?? parentOrDefaultContext.labels,
      initialTouched: originalState,
    }),
    [
      attributes,
      operators,
      parentOrDefaultContext.operators,
      parentOrDefaultContext.combinators,
      parentOrDefaultContext.maxDepth,
      parentOrDefaultContext.labels,
      combinators,
      maxDepth,
      labels,
      originalState,
    ]
  );

  // keep ref just for preventing bad use
  // if a non-stable onChange would:
  // state changed -> call onChange
  // onChange changed -> call onChange (in loop)
  const callback = useRef(onChange);
  React.useEffect(() => {
    callback.current = onChange;
  }, [onChange]);
  // Propagate the change if the query is modified.
  React.useEffect(() => {
    if (callback.current && !originalState) {
      callback.current(clearNodeIds(state) as Query);
    }
  }, [state, originalState]);

  const onConfirmHandler = () => {
    if (pendingAction) {
      askAction(undefined);
      pendingAction.actions.forEach((action) => dispatchAction(action));
    }
  };

  const onCancelHandler = () => {
    askAction(undefined);
  };

  const onSelectLocationHandler = (data: LocationFormData) => {
    locationDialog?.onSelection?.(data);
    selectLocation(undefined);
  };

  const onSelectLocationCancelHandler = () => {
    selectLocation(undefined);
  };

  return (
    <Context.Provider value={context}>
      <RuleGroup
        level={0}
        id={state.id as number}
        combinator={state.combinator}
        rules={state.rules}
      />
      <ConfirmationDialog
        isOpen={pendingAction != null}
        onConfirm={onConfirmHandler}
        onCancel={onCancelHandler}
        title={pendingAction?.dialog.dialogTitle || ""}
        message={pendingAction?.dialog.dialogMessage || ""}
        confirmButtonLabel={pendingAction?.dialog.dialogConfirm || ""}
        cancelButtonLabel={pendingAction?.dialog.dialogCancel || ""}
        closeButtonTooltip={pendingAction?.dialog.dialogCloseTooltip || ""}
      />
      <SelectLocationDialog
        isOpen={locationDialog != null}
        location={locationDialog?.location}
        marker={marker}
        onSelection={onSelectLocationHandler}
        onCancel={onSelectLocationCancelHandler}
      />
    </Context.Provider>
  );
};

const MemoQueryBuilder = React.memo(QueryBuilder, (prevProps, nextProps) => {
  // this component is totally uncontrolled.
  // it only re-renders on internal state changes AND:
  // - when attributes prop changes (current query is reset, metadata changed - marker prop can also change)
  // - when labels prop changes (react to language changes)
  // - when onChange prop changes (passed callback can change based on parent's component closure)
  return (
    prevProps.attributes === nextProps.attributes &&
    prevProps.labels === nextProps.labels &&
    prevProps.onChange === nextProps.onChange
  );
});

export default MemoQueryBuilder;
